import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationStart, Params, Router, RouterLink } from '@angular/router';
import {
  AssetDto,
  AssetImageTypeDataDto,
  GalleryDto,
  GalleryFeedbackOptions,
  GalleryTheme,
  Section,
} from '@nusign/picflow-backend-types';
import {
  Subject,
  Subscription,
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  mergeWith,
  of,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { fadeDownIfSubtle } from '../../../animations/fade-down-subtle.if.animation';
import { Rect } from '../../../classes/rect.class';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { UploadFile, UploadObj } from '../../../classes/upload-obj.class';
import { assetLoadSettings, browserRenderableExtensions } from '../../../consts';
import { AssetContextMenuComponent } from '../../../gallery-shared/components/asset-context-menu/asset-context-menu.component';
import { LayoutAssetComponent } from '../../../gallery-shared/components/layout-asset/layout-asset.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { singleFilter } from '../../../gallery-shared/lib/filter-assets';
import { CanDisplayAssetPipe } from '../../../gallery-shared/pipes/can-display-asset.pipe';
import { CanDownloadAssetPipe } from '../../../gallery-shared/pipes/can-download-asset.pipe';
import { GROWTHBOOK } from '../../../growthbook';
import { arraysEqual } from '../../../lib/arrays-equal';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { changeCoverAsset } from '../../../lib/change-cover-asset';
import { copy } from '../../../lib/copy';
import { debounce } from '../../../lib/debounce';
import { Exif, getCaptureTime } from '../../../lib/extract-exif';
import { getImageAndUrlFromFileObject, resizeImage } from '../../../lib/get-and-resize-image-file';
import { randomString } from '../../../lib/random-string';
import { sleep } from '../../../lib/sleep';
import { AssetSupportsTransparencyPipe } from '../../../pipes/asset-supports-transparency.pipe';
import { AiService } from '../../../services/ai.service';
import { AssetMoveService } from '../../../services/asset-move.service';
import { AssetPreGenerateService } from '../../../services/asset-pre-generate.service';
import { AssetService } from '../../../services/asset.service';
import { BrowserService } from '../../../services/browser.service';
import { ContextMenuService } from '../../../services/context-menu.service';
import { ConvertRawPreviewService } from '../../../services/convert-raw-preview.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { ToastService } from '../../../services/toast.service';
import { FromUrlFile } from '../../../services/upload-from-url.service';
import { UploadService } from '../../../services/upload.service';
import { ActivityBadgeComponent } from '../../../shared-extra/components/activity-badge/activity-badge.component';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { StickyComponent } from '../../../shared-extra/components/sticky/sticky.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { AssetUrlPipe } from '../../../shared/pipes/asset-url.pipe';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { FailedAssetVersionUrlPipe } from '../../../shared/pipes/failed-asset-version-url.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { FakeAssetDto, FakeGalleryItem, GalleryItem } from '../../classes/gallery-item.class';
import { ManualConfirmDialogComponent } from '../../dialogs/manual-confirm-dialog/manual-confirm-dialog.component';
import { ShareAssetDialogComponent } from '../../dialogs/share-asset-dialog/share-asset-dialog.component';
import { getAssetSectionHeight } from '../../lib/get-asset-section-height';
import { adaptGalleryWidth } from '../../lib/get-gallery-width';
import { sortAssetSection } from '../../lib/sort-asset-section';
import { InferredContextOpenPipe } from '../../pipes/inferred-context-open.pipe';
import { AssetTransitionService } from '../../services/asset-transition.service';
import { LayoutGridService } from '../../services/layout-grid.service';
import { LayoutMosaicColumnsService } from '../../services/layout-mosaic-columns.service';
import { LayoutMosaicRowsService } from '../../services/layout-mosaic-rows.service';
import { AssetDownloadButtonComponent } from '../asset-download-button/asset-download-button.component';
import { AssetsSectionMenuComponent } from '../assets-section-menu/assets-section-menu.component';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { UploadSourceMenuComponent } from '../upload-source-menu/upload-source-menu.component';

@Component({
  selector: 'app-assets-section',
  templateUrl: './assets-section.component.html',
  styleUrls: ['./assets-section.component.scss'],
  animations: [trigger('assetButton', fadeDownIfSubtle)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AssetUrlPipe],
  imports: [
    DropComponent,
    SectionContainerComponent,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    ContextMenuComponent,
    AssetsSectionMenuComponent,
    TransitionPreventorDirective,
    InferredHoverDirective,
    AssetContextMenuComponent,
    RouterLink,
    ActivityBadgeComponent,
    AssetVersionUrlPipe,
    TranslatePipe,
    AsyncPipe,
    AssetSupportsTransparencyPipe,
    StickyComponent,
    UploadSourceMenuComponent,
    LayoutAssetComponent,
    AssetDownloadButtonComponent,
    CanDownloadAssetPipe,
    InferredContextOpenPipe,
    CanDisplayAssetPipe,
    FailedAssetVersionUrlPipe,
  ],
})
export class AssetsSectionComponent implements OnInit, OnChanges, OnDestroy {
  private check = inject(ChangeDetectorRef);
  private layoutGrid = inject(LayoutGridService);
  private layoutRows = inject(LayoutMosaicRowsService);
  private layoutColumns = inject(LayoutMosaicColumnsService);
  private assetService = inject(AssetService);
  private uploadService = inject(UploadService);
  private sectionService = inject(SectionService);
  private assetUrl = inject(AssetUrlPipe);
  private galleryService = inject(GalleryService);
  private router = inject(Router);
  private dialog = inject(DialogService);
  private toasts = inject(ToastService);
  private browserService = inject(BrowserService);
  private assetPreGenerateService = inject(AssetPreGenerateService);
  private assetTransitionService = inject(AssetTransitionService);
  private route = inject(ActivatedRoute);
  private storage = inject(StorageService);
  private converRawPreviewService = inject(ConvertRawPreviewService);
  public state = inject(StateService);
  private assetMoveService = inject(AssetMoveService);
  public contextService = inject(ContextMenuService);
  public galleryThemeService = inject(GalleryThemeService);
  private aiService = inject(AiService);
  uploadIdPrefix = randomString(8);
  @ViewChild('assetGallery', { static: false }) assetGallery: ElementRef<HTMLElement>;
  @ViewChild('sectionContainer') sectionContainer: SectionContainerComponent;
  @ViewChild('sectionContainer', { read: ElementRef }) sectionContainerEl: ElementRef<HTMLElement>;
  @ViewChild('sectionContent') sectionContent: ElementRef<HTMLDivElement>;
  @ViewChild('upload') upload: ElementRef;
  readonly theme = input.required<GalleryTheme>();
  readonly darkBackground = input<boolean>(undefined);
  readonly section = input<Section>(undefined);
  readonly sectionIndex = input<number>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly sections = input<Section[]>(undefined);
  readonly allowedAndEnabledFeedbackOptions = input<GalleryFeedbackOptions[]>(undefined);
  readonly approvedAssetCount = input<number>(undefined);
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly filters = input<Filter[]>([]);
  @Output() onMove: EventEmitter<{
    clientX: number;
    clientY: number;
    item: GalleryItem;
    inSection: boolean;
    sectionComponent: AssetsSectionComponent;
  }> = new EventEmitter();
  @Output() onDrop: EventEmitter<boolean> = new EventEmitter();
  @Output() onCoverChange: EventEmitter<void> = new EventEmitter();
  @Output() onSectionDeleted: EventEmitter<Section> = new EventEmitter();
  @Output() addSection: EventEmitter<number> = new EventEmitter();
  noGalleryAnimation = true;
  sectionHeight: number;
  private assets = toSignal(
    toObservable(this.section).pipe(
      filter(Boolean),
      switchMap(section => this.assetService.getAssetsObservable(section.id, section.gallery))
    )
  );
  items: GalleryItem[];
  fakeItems: FakeGalleryItem[] = [];
  visibleItems: GalleryItem[] = [];
  draggingItem: GalleryItem;
  draggingItemDropOnSubGallery = false;
  lastDraggingItem: GalleryItem;
  potentialDraggingItem: GalleryItem;
  mousePosX: number;
  mousePosY: number;
  mousePosOffsetX: number;
  mousePosOffsetY: number;
  ignoreMouseMovement = false;
  sectionWidth = signal<number>(undefined);
  scrollingPage: boolean;
  private uploadingCount = 0;
  forceAssetsView = false;
  debugMode: boolean;
  deadZone: Rect;
  dragging = false;
  hoverItem: GalleryItem;
  hoverContextOpenItem: GalleryItem;
  assetPage: boolean;
  gettingAssets = true;
  destroyed = false;
  sortings = ['manual', 'asset_name', 'created_at', 'capture_time', 'random'];
  sorting: string;
  preDragOrder: string[];
  isDesktop = ScreenSizes.screenSize() === 'desktop';
  destroyed$ = new Subject<void>();
  assetQueryParams: Params = { autoplay: true };
  fakeItemsWatcher: Subscription;
  assetPreview: { [key: string]: { dimensions: Rect; src: string } } = {};

  allItems = computed(() => {
    const assets = this.assets();
    if (!assets) return;
    return assets.map(asset => new GalleryItem(asset));
  });

  constructor() {
    effect(() => {
      const allItems = this.allItems();
      if (!allItems) return;
      this.handleNewItems();
    });

    effect(() => {
      const allItems = this.allItems();
      if (!allItems) return;
      this.setupFakeItems();
    });
  }

  private async handleNewItems() {
    this.calculateAllItemPositions();
    this.check.detectChanges();
    await sleep(100);
    this.noGalleryAnimation = false;
    this.gettingAssets = false;
    this.check.detectChanges();
  }

  aiUploadWatcher = this.aiService.onNewImages$
    .pipe(
      takeUntil(this.destroyed$),
      filter(data => data?.sectionId === this.section()?.id && !!data?.urls.length)
    )
    .subscribe(event => {
      const files = event.urls.map((url, i) => {
        const name = this.aiService.getFileName(event.params.model, i);

        return <FromUrlFile>{
          name,
          url,
          size: 100_000,
          source: 'url',
        };
      });

      this.uploadAssets(files);
    });

  @HostListener('window:scroll', ['$event']) windowScroll() {
    this.calcInView();
  }

  @HostListener('window:resize', ['$event']) winResize() {
    if (!this.allItems() && !this.fakeItems) return;
    this.calculateAllItemPositions();
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';
    this.check.detectChanges();
  }

  public growthBook = inject(GROWTHBOOK);

  async ngOnInit() {
    this.sectionHeight = this.sectionService.sectionHeightCache[this.section().id];
    this.debugMode = this.growthBook.isOn('asset-debug-mode');
    this.sectionWidth.set(adaptGalleryWidth(this.section().width ?? this.theme().section.width));
    this.sorting = this.section().sorting || 'manual';

    if (this.uploadService.toUploadCache) {
      const files = this.uploadService.toUploadCache;
      this.uploadService.toUploadCache = undefined;
      await this.uploadAssets(files);
    }

    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe(event => {
      if (!(event instanceof NavigationStart)) return;
      delete this.draggingItem;
      delete this.potentialDraggingItem;
      this.assetPage = this.browserService.urlIsAssetUrl(event.url);
      this.calcInView();
    });

    this.assetMoveService.assetMove$.pipe(takeUntil(this.destroyed$), filter(Boolean)).subscribe(async event => {
      if (!this.allItems()) return;
      const item = this.allItems().find(item => item.asset.id === event.assetId);
      if (!item) return;
      this.onMovedOutInitial(item.asset);
      this.onMovedOutConfirm();
    });

    this.sectionService.onSectionUpdated$
      .pipe(mergeWith(this.sectionService.optimisticUpdates), takeUntil(this.destroyed$), filter(Boolean))
      .subscribe(section => {
        if (section.id !== this.section().id) return;
        this.calculateAllItemPositions();
      });

    this.assetService.updates.pipe(takeUntil(this.destroyed$)).subscribe(async event => {
      if (!event) return;
      if (!this.allItems()) return;

      if (event.asset.section !== this.section().id) {
        const index = this.allItems().findIndex(item => item.asset.id === event.asset.id);
        if (index > -1) {
          this.allItems().splice(index, 1);
          this.calculateAllItemPositions();
        }
        return;
      }
      if (event.action === 'deleted') {
        const index = this.allItems().findIndex(item => item.asset.id === event.asset.id);
        if (index !== -1) {
          this.allItems().splice(index, 1);
          this.calculateAllItemPositions();
        }
      }
      if (event.action === 'updated') {
        const index = this.allItems().findIndex(item => item.asset.id === event.asset.id);
        const newAsset = event.asset;
        if (index !== -1) {
          const oldAsset = this.allItems()[index].asset;
          const positionChanged = oldAsset.position !== newAsset.position;
          Object.assign(oldAsset, newAsset);
          const preview = this.assetPreview[oldAsset.version_data.asset_name];
          if (positionChanged || !preview) {
            this.calculateAllItemPositions();
          }
        } else {
          const item = new GalleryItem(event.asset);
          this.allItems().push(item);
          const applied = this.transferFakeItemToRealItem(item);
          this.calculateAllItemPositions(applied);
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.allItems()) return;
    if (changes.filters) {
      const filtersValue = this.filters();
      const filters = filtersValue.length ? JSON.stringify(filtersValue) : undefined;
      this.assetQueryParams = { filters, autoplay: true };
    }
    if (changes.section) {
      this.calculateAllItemPositions();
    }
  }

  private setupFakeItems() {
    if (this.fakeItemsWatcher) return;
    this.fakeItemsWatcher = this.uploadService.uploads$
      .pipe(
        takeUntil(this.destroyed$),
        filter(Boolean),
        distinctUntilChanged((a, b) => {
          if (!this.fakeItems.length) return false;
          return a.length === b.length;
        })
      )
      .subscribe(uploads => {
        const allItems = this.allItems();
        const gallery = this.gallery();
        const section = this.section();
        if (!gallery) return;
        if (!allItems) return;
        const galleryUploads = uploads.filter(
          upload =>
            upload.galleryId === gallery.id &&
            upload.sectionId === section.id &&
            !allItems.find(item => item.asset.version_data.asset_name === upload.file.name)
        );
        if (!galleryUploads.length) return;

        galleryUploads.forEach(upload => {
          const currentFakeItem = this.fakeItems?.find(
            fakeItem => fakeItem.asset.version_data.asset_name === upload.file.name
          );
          if (currentFakeItem) return;
          const item = new FakeGalleryItem(<FakeAssetDto>{
            id: upload.id,
            status: 'no_status',
            version_data: {
              status: 'pending',
              asset_name: upload.file.name,
              data: {
                height: 0,
                width: 0,
                exif: {},
              },
            },
          });
          if (this.canGeneratePreview(upload)) {
            item.previewUpload = upload;
          }
          this.fakeItems.push(item);
        });
        this.calculateAllItemPositions();
        this.check.detectChanges();
      });

    this.uploadService.preGenerated$.pipe(takeUntil(this.destroyed$), filter(Boolean)).subscribe(asset => {
      const preview = this.assetPreview[asset.version_data.asset_name];
      if (preview) {
        delete this.assetPreview[asset.version_data.asset_name];
        URL.revokeObjectURL(preview.src);
      }
    });
  }

  sortItemsFromExternal() {
    const items = [...(this.allItems() || []), ...(this.fakeItems || [])];
    this.sortItemsBasedOnAssetOrder(
      sortAssetSection(
        items.map(i => i.asset),
        this.sorting
      )
    );
  }

  sortItems(items: GalleryItem[]) {
    const assets = sortAssetSection(
      items.map(i => i.asset),
      this.sorting
    );
    const assetIndexMap = new Map(assets.map((asset, index) => [asset.id, index]));
    return items.sort((a, b) => assetIndexMap.get(a.asset.id) - assetIndexMap.get(b.asset.id));
  }

  private internalCalcItemPos(items: GalleryItem[]) {
    const gridLayout = this.section().grid_layout ?? this.theme().section.grid_layout;
    if (gridLayout === 'grid') {
      return this.layoutGrid.calculateAllItemPositions(this.section(), this.theme(), items);
    }
    if (gridLayout === 'rows') {
      return this.layoutRows.calculateAllItemPositions(this.section(), this.theme(), items);
    }
    if (gridLayout === 'columns') {
      return this.layoutColumns.calculateAllItemPositions(this.section(), this.theme(), items);
    }
    console.error('Unsupported layout', gridLayout);
  }

  private filterAssets(items: GalleryItem[], filters: Filter[]) {
    if (!items) return;
    if (!this.galleryPermissions()?.can_review_if_authenticated) {
      return items.filter(i => i.asset.status !== 'rejected');
    } else {
      return items.filter(item => !filters.find(filter => singleFilter(filter, item.asset)));
    }
  }

  private cachedSortedItems: GalleryItem[];
  calculateAllItemPositions(simple = false, sort = true): void {
    if (!this.allItems() && !this.fakeItems) return;
    let draggingAssetCopy: GalleryItem;
    if (this.draggingItem) draggingAssetCopy = copy(this.draggingItem);

    this.items = this.filterAssets(this.allItems(), this.filters());

    if (!simple) {
      const itemsIncludingFake = [...(this.items || []), ...(this.fakeItems || [])];
      this.cachedSortedItems = sort ? this.sortItems(itemsIncludingFake) : itemsIncludingFake;
      const width = this.internalCalcItemPos(this.cachedSortedItems);

      if (width) {
        const sectionWidth = adaptGalleryWidth(this.section().width ?? this.theme().section.width);
        this.sectionWidth.set(Math.min(width, sectionWidth));
      }
    }

    this.check.detectChanges();
    const spacing = this.section().spacing ?? this.theme().section.spacing;
    this.sectionHeight = getAssetSectionHeight(
      this.cachedSortedItems,
      spacing,
      this.section().show_filenames ?? this.theme().section.show_filenames
    );
    this.sectionService.sectionHeightCache[this.section().id] = this.sectionHeight;

    if (draggingAssetCopy) {
      if (draggingAssetCopy.dimensions.width !== this.draggingItem.dimensions.width) {
        this.mousePosOffsetX *= (1 / draggingAssetCopy.dimensions.width) * this.draggingItem.dimensions.width;
        this.mousePosOffsetY *= (1 / draggingAssetCopy.dimensions.height) * this.draggingItem.dimensions.height;
      }
    }
    this.calcInView();
  }

  async uploadAssetEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    await this.uploadAssets(Array.from(files));
    if (this.upload) this.upload.nativeElement.value = '';
  }

  private canGeneratePreview(upload: UploadObj) {
    if (upload.status === 'done' || upload.status === 'error') return false;
    if ('url' in upload.file) return true;
    const extension = upload.file.name.split('.').pop();
    const browserRenderable = browserRenderableExtensions.includes(extension);
    const canExtractPreviewFromRaw = this.converRawPreviewService.canExtractPreview(upload.file, 'jpg');

    const isRawFile = !browserRenderable;

    if (!browserRenderable && !canExtractPreviewFromRaw) return false;
    if (!isRawFile && upload.file.size > 1024 * 1024 * 8) return false; // 10mb
    if (isRawFile && upload.file.size > 1024 * 1024 * 50) return false; // 50mb

    if ('source' in upload.file) return false;
    return true;
  }

  private generatePreviewForFile(file: UploadFile) {
    if ('url' in file) {
      return from(resizeImage(file.url, 800, 'normal')).pipe(catchError(() => of(null)));
    }
    if ('source' in file) return of(null);
    const isRawFile = !browserRenderableExtensions.includes(file.name.split('.').pop());

    if (isRawFile) {
      return this.converRawPreviewService.extractPreview(file, 'jpg', 0.8).pipe(
        switchMap(d => {
          if ('status' in d) return of(null);
          if (d.error) throw d.error;
          const src = d.src;
          return from(resizeImage(src, 800, 'normal'));
        })
      );
    }

    return from(getImageAndUrlFromFileObject(file));
  }

  private async generatePreviewForUpload(upload: UploadObj) {
    let generated = false;
    if (this.growthBook.isOn('disable-image-previews')) return;
    try {
      const { image, src } = await firstValueFrom(this.generatePreviewForFile(upload.file));
      const dimensions = new Rect(0, 0, image.naturalWidth, image.naturalHeight);
      this.assetPreview[upload.file.name] = {
        dimensions,
        src,
      };

      const item = [...this.allItems(), ...this.fakeItems].find(
        item => item.asset.version_data.asset_name === upload.file.name
      );
      if (item) {
        item.asset.version_data.data = {
          height: dimensions.height,
          width: dimensions.width,
          exif: <Exif>{},
        };
        generated = true;
      }

      sleep(1000 * 60 * 10).then(() => {
        delete this.assetPreview[upload.file.name];
        URL.revokeObjectURL(src);
      });
      return generated;
    } catch (err) {
      console.debug(err);
    }
  }

  private transferFakeItemToRealItem(item: GalleryItem) {
    const fakeItem = this.fakeItems.find(
      fakeItem => fakeItem.asset.version_data.asset_name === item.asset.version_data.asset_name
    );

    if (!fakeItem) return false;

    item.dimensions = fakeItem.dimensions;
    item.bounds = fakeItem.bounds;
    if (fakeItem.asset.version_data.data?.height) {
      item.previewUpload = fakeItem.previewUpload;
      if (!item.asset.version_data.data?.height) {
        item.asset.version_data.data = fakeItem.asset.version_data.data;
      }
    }

    this.fakeItems.splice(this.fakeItems.indexOf(fakeItem), 1);
    return true;
  }

  async uploadAssets(files: UploadFile[]) {
    if (!this.galleryPermissions().can_edit) return;
    this.uploadingCount += 1;
    this.noGalleryAnimation = true;
    this.check.detectChanges();

    this.assetService
      .getAssetsObservable(this.section().id, this.gallery().id)
      .pipe(take(1))
      .subscribe(allAssets => {
        this.uploadService
          .managedUpload(files, this.gallery(), this.section().id, allAssets, this.sorting)
          .subscribe(async next => {
            if (this.destroyed) return;

            if (next.type === 'created') {
              const item = new GalleryItem(next.data.asset);
              this.allItems().push(item);
              const applied = this.transferFakeItemToRealItem(item);
              this.calculateAllItemPositions(applied);
            }

            if (next.type === 'uploaded' && next.data.asset) {
              const gallery = this.gallery();
              if (!gallery.teaser?.uuid) {
                const asset = await this.uploadService.waitForAssetProcessing(next.data.asset.id);
                if (!gallery.teaser?.uuid && canDisplayAsset(asset)) {
                  gallery.teaser = { position: '50% 50%' };
                  changeCoverAsset(gallery.teaser, asset);

                  this.galleryService.updateGallery(gallery.id, {
                    teaser: gallery.teaser,
                  });
                }
              }
            }

            if (next.type === 'remove') {
              const index = this.allItems().findIndex(
                item => item.asset.version_data.asset_name === next.data.upload.file.name
              );
              if (index !== -1) this.allItems().splice(index, 1);
              const fakeIndex = this.fakeItems.findIndex(
                item => item.asset.version_data.asset_name === next.data.upload.file.name
              );
              if (fakeIndex !== -1) this.fakeItems.splice(fakeIndex, 1);
              this.calculateAllItemPositions();
            }
          })
          .add(() => {
            this.uploadingCount -= 1;
            this.noGalleryAnimation = false;
            this.fakeItems = [];
            this.calculateAllItemPositions();
          });
      });
  }

  onNewVersion() {
    this.calculateAllItemPositions();
  }

  onMovedOutInitial(asset: AssetDto) {
    const itemIndex = this.allItems().findIndex(_item => _item.asset.id === asset.id);
    if (itemIndex === -1) {
      // eslint-disable-next-line no-debugger
      debugger;
      return;
    }
    const item = this.allItems().splice(itemIndex, 1)[0];
    this.calculateAllItemPositions();
    return item;
  }

  async onMovedOutConfirm() {
    await this.handleAssetOrderChange();
  }

  onMovedOutCancel(item: GalleryItem) {
    this.allItems().push(item);
    this.calculateAllItemPositions();
  }

  downloadClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  grapItemMouse(event: MouseEvent, item: GalleryItem) {
    if ((event.target as HTMLDivElement)?.classList?.contains('asset-name')) return;

    if (!this.galleryPermissions().can_edit) return;
    if (event.buttons !== 1) return false;
    event.preventDefault();
    event.stopPropagation();
    this.grapItem(event.clientX, event.clientY, item);
    return false;
  }

  grapItem(clientX: number, clientY: number, item: GalleryItem) {
    if (!this.allItems()) return;
    if (!this.assetGallery) return;
    this.preDragOrder = this.allItems().map(item => item.asset.id);
    this.potentialDraggingItem = item;
    const galleryBox = this.assetGallery.nativeElement.getBoundingClientRect();
    const left = item.dimensions.x + galleryBox.left;
    this.mousePosOffsetX = clientX - left;
    const top = item.dimensions.y + galleryBox.top;
    this.mousePosOffsetY = clientY - top;
    this.mousePosX = clientX;
    this.mousePosY = clientY;
  }

  mouseMove(event: MouseEvent) {
    if (event.buttons !== 1) return;
    this.dragMove(event.clientX, event.clientY);
  }

  dragMove(clientX: number, clientY: number) {
    this.dragging = true;
    if (this.ignoreMouseMovement) return;
    if (!this.potentialDraggingItem && !this.draggingItem) return;

    // If we are not dragging an item then we check if the mouse has moved at least a few pixels before we set draggingItem
    if (!this.draggingItem && this.potentialDraggingItem) {
      const mousePosXDiff = clientX - this.mousePosX;
      const mousePosYDiff = clientY - this.mousePosY;
      if (Math.abs(mousePosXDiff + mousePosYDiff) < 5) return;
      this.draggingItem = this.potentialDraggingItem;
    }

    this.sectionContainer.hover = false;

    this.mousePosX = clientX;
    this.mousePosY = clientY;
    this.handleItemMove(clientX, clientY);
  }

  mouseUp() {
    this.dragEnd();
  }

  handleItemMove = debounce((clientX: number, clientY: number) => {
    if (this.deadZone) {
      const galleryBox = this.assetGallery.nativeElement.getBoundingClientRect();
      const x = clientX - galleryBox.x;
      const y = clientY - galleryBox.y;
      if (x > this.deadZone.x && x < this.deadZone.x + this.deadZone.width) {
        if (y > this.deadZone.y && y < this.deadZone.y + this.deadZone.height) {
          this.onMove.emit({ clientX, clientY, item: this.draggingItem, inSection: true, sectionComponent: this });
          return;
        }
      }
    }

    if (!this.draggingItem) return;
    const dragPos = this.allItems().indexOf(this.draggingItem);
    const { index, outside } = this.getItemInsertIndex(clientX, clientY);
    this.onMove.emit({ clientX, clientY, item: this.draggingItem, inSection: !outside, sectionComponent: this });

    if (index === -1) return;
    if (dragPos !== -1 && index !== dragPos) {
      const oldItem = this.allItems()[index];
      if (oldItem) this.deadZone = oldItem.bounds.copy();
      const item = this.allItems().splice(dragPos, 1)[0];
      if (item) this.allItems().splice(index, 0, item);
    }
    this.calculateAllItemPositions(false, false);
  }, 5);

  getItemInsertIndex(clientX: number, clientY: number): { index: number; outside: boolean } {
    const allItems = this.allItems();
    if (!this.assetGallery || !this.sectionContent || !allItems) return { index: 0, outside: true };
    if (this.debugMode) allItems.forEach(item => (item.color = undefined));
    const galleryBox = this.assetGallery.nativeElement.getBoundingClientRect();
    const sectionBox = this.sectionContent.nativeElement.getBoundingClientRect();
    const outsideAbove = clientY < sectionBox.top;
    const outsideBelow = clientY > sectionBox.top + sectionBox.height;
    const outside = outsideAbove || outsideBelow;

    if (!allItems.length) return { index: 0, outside };
    if (outside) return { index: this.draggingItem.asset.position, outside };

    const itemsWithinYRange = allItems.filter(item => {
      const inFirstRow = item.bounds.y === 0;
      const offsetY = clientY - (item.bounds.y + galleryBox.top);
      if (!inFirstRow && offsetY < 0) return false;
      if (offsetY > item.bounds.height) return false;
      return true;
    });

    itemsWithinYRange.sort((a, b) => (a.bounds.x > b.bounds.x ? 1 : -1));

    if (this.debugMode)
      itemsWithinYRange.forEach(item => {
        item.color = 'in-row';
        const firstInRow = item === itemsWithinYRange[0];
        const lastInRow = item === itemsWithinYRange[itemsWithinYRange.length - 1];
        if (firstInRow) item.color = 'first';
        if (lastInRow) item.color = 'last';
      });

    const item = itemsWithinYRange.find(item => {
      const firstInRow = item === itemsWithinYRange[0];
      const lastInRow = item === itemsWithinYRange[itemsWithinYRange.length - 1];

      const offsetX = clientX - (item.bounds.x + galleryBox.left);
      if (firstInRow || lastInRow) {
        if (firstInRow) return offsetX < item.bounds.width;
        if (lastInRow) return offsetX >= 0;
      }
      return offsetX < item.bounds.width;
    });

    if (!item) {
      console.warn('No item');
      if (itemsWithinYRange) return { index: allItems.length, outside };
      if (itemsWithinYRange) return { index: -1, outside };
    }

    if (this.debugMode) item.color = 'selected';

    const index = allItems.indexOf(item);
    return { index, outside };
  }

  async changeAssetSortingToManual() {
    if (this.sorting === 'manual') return;
    const section = this.section();
    section.sorting = 'manual';
    this.sorting = section.sorting;
    this.toasts.add('Asset order changed to Manual', 'info');

    const sortedAssets = this.allItems().map(item => item.asset);
    const changes: { asset: AssetDto; position: number }[] = [];
    sortedAssets.forEach((asset, i) => {
      if (asset.position === i) return;
      changes.push({ asset, position: i });
    });

    sortedAssets.map((asset, i) => (asset.position = i));

    this.calculateAllItemPositions();

    await Promise.all([
      firstValueFrom(this.sectionService.updateSection(section, { sorting: 'manual' })),
      ...changes.map(data => {
        return this.assetService.updateAsset(
          data.asset,
          { position: data.position },
          this.gallery().id,
          this.section().id
        );
      }),
    ]);
  }

  async dragEnd() {
    if (!this.allItems()) return;
    this.dragging = false;

    const order = this.allItems().map(item => item.asset.id);

    this.onDrop.emit(!this.preDragOrder || !arraysEqual(order, this.preDragOrder));
    this.deadZone = undefined;
    delete this.potentialDraggingItem;
    if (this.draggingItem) {
      this.lastDraggingItem = this.draggingItem;
      delete this.draggingItem;
    }
    // Note: Order change is handled by parent component (edit-gallery)
  }

  async handleAssetOrderChange() {
    this.check.detectChanges();
    if (this.uploadingCount > 0) return;
    if (!this.allItems()) return;
    let sortedAssets = this.allItems().map(item => item.asset);
    if (this.sorting !== 'manual') {
      sortedAssets = sortAssetSection(
        this.allItems().map(i => i.asset),
        this.sorting
      );
    } else {
      await this.assetService.handleOrderChange(sortedAssets, this.gallery().id, this.section().id);
    }
    // this.sortItemsBasedOnAssetOrder(sortedAssets);
    // this.calculateAllItemPositions();
  }

  sortItemsBasedOnAssetOrder(assets: AssetDto[]) {
    const assetIndexMap = new Map(assets.map((asset, index) => [asset.id, index]));
    this.allItems()?.sort((a, b) => assetIndexMap.get(a.asset.id) - assetIndexMap.get(b.asset.id));
    this.check.detectChanges();
  }

  async calcInView(isRetry = false) {
    if (this.destroyed) return;
    if (this.assetPage) return;
    await sleep(0);
    if (!this.assetGallery) {
      if (!isRetry) setTimeout(() => this.calcInView(true), 100);
      return;
    }
    if (!this.items && !this.fakeItems) return;

    const visibleItems = [] as GalleryItem[];
    const preGenerateItems: GalleryItem[] = [];
    const sectionBox = this.assetGallery.nativeElement.getBoundingClientRect();
    const items = [...(this.items || []), ...(this.fakeItems || [])];
    items.forEach(item => {
      const top = sectionBox.top + item.dimensions.y - 64;
      const bottom = sectionBox.top + item.dimensions.y + item.dimensions.height - 64;
      if (
        this.isInView(
          top,
          bottom,
          assetLoadSettings.galleryScreensToPreloadBefore,
          assetLoadSettings.galleryScreensToPreloadAfter
        )
      ) {
        visibleItems.push(item);
      } else if (
        this.isInView(
          top,
          bottom,
          assetLoadSettings.galleryScreensToPreloadBefore,
          assetLoadSettings.galleryScreensToPreloadAfter + assetLoadSettings.galleryScreensToPreGenerate
        )
      ) {
        preGenerateItems.push(item);
      }
    });

    preGenerateItems.forEach(async item => {
      const url = await this.calcAssetUrl(item);
      if (url) this.assetPreGenerateService.preloadAsset(url);
    });

    const previewItems = visibleItems.filter(item => item.previewUpload);
    let generated = false;
    for (const item of previewItems) {
      const file = item?.previewUpload?.file;
      if (file && !this.assetPreview[file.name]) {
        const previewUpload = item.previewUpload;
        delete item.previewUpload;
        const g = await this.generatePreviewForUpload(previewUpload);
        if (g) generated = true;
      }
    }
    if (generated) {
      this.calculateAllItemPositions();
    }
    this.visibleItems = visibleItems;
    this.pregenerateHighRes();
    this.check.detectChanges();
  }

  public pregenerateHighRes = debounce(() => {
    if (!this.assetGallery) return;
    const onScreenItems = [];
    const sectionBox = this.assetGallery.nativeElement.getBoundingClientRect();
    this.allItems().forEach(item => {
      const top = sectionBox.top + item.dimensions.y - 64;
      const bottom = sectionBox.top + item.dimensions.y + item.dimensions.height - 64;
      if (assetLoadSettings.preGenerateHighResFromList && this.isInView(top, bottom, 0, 0)) onScreenItems.push(item);
    });

    // Pre generating assets for asset page
    const sideMenuOpen = !!this.route.snapshot.fragment || !!this.storage.get<string>('asset-sidebar');
    onScreenItems.forEach(async item => {
      if (!canDisplayAsset(item.asset)) return;
      const bounds = this.assetTransitionService.getAssetPageAssetBounds(item.asset, sideMenuOpen);
      const url = await this.assetUrl.transform(item.asset, '', bounds.width, false);
      this.assetPreGenerateService.preloadAsset(url);
    });
  }, 250);

  private isInView(
    top: number,
    bottom: number,
    bufferBeforeMultiplier: number,
    bufferAfterMultiplier: number
  ): boolean {
    const visibleWindowHeight = window.innerHeight - 64;
    const beforeBuffer = visibleWindowHeight * bufferBeforeMultiplier;
    const afterBuffer = visibleWindowHeight * bufferAfterMultiplier;
    if (bottom < -beforeBuffer) return false;
    if (top > visibleWindowHeight + afterBuffer) return false;
    return true;
  }

  public async changeAssetSorting(sortBy: string) {
    if (sortBy === 'manual' && this.sorting !== 'manual') {
      if (this.allItems().length > 100) {
        const resp = await this.dialog.open(ManualConfirmDialogComponent);
        if (!resp) return;
      }
      return this.changeAssetSortingToManual();
    }

    if (sortBy === 'random') return this.refreshRandom();
    if (sortBy === this.sorting.split('|')[0]) return;
    const section = this.section();
    section.sorting = sortBy;
    this.sorting = section.sorting;

    this.calculateAllItemPositions();

    await firstValueFrom(this.sectionService.updateSection(section, { sorting: sortBy }));
  }

  public async refreshRandom() {
    this.changeAssetSorting(`random|${randomString(4)}`);
  }

  public async flipSorting() {
    if (this.sorting.startsWith('-')) {
      this.changeAssetSorting(this.sorting.substring(1));
    } else {
      this.changeAssetSorting(`-${this.sorting}`);
    }
  }

  async calcAssetUrl(item: GalleryItem) {
    if (!canDisplayAsset(item.asset)) return;
    return await this.assetUrl.transform(item.asset, '', item.dimensions.width, true);
  }

  beforeSettingsChange() {
    this.noGalleryAnimation = true;
    this.afterSettingsChange();
    this.check.detectChanges();
  }

  afterSettingsChange = debounce(() => {
    this.noGalleryAnimation = false;
    this.check.detectChanges();
  }, 50);

  openShareDialog(asset: AssetDto) {
    this.dialog.open(ShareAssetDialogComponent, { gallery: this.gallery(), asset });
  }

  private allowDownload(asset: AssetDto) {
    if (this.galleryPermissions().can_edit) return true;
    const gallery = this.gallery();
    if (!gallery.download_single_asset) return false;
    if (gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  }

  assetRightClick(asset: AssetDto, event: Event) {
    if (this.allowDownload(asset)) return true;
    event.preventDefault();
    event.stopPropagation();
  }

  setHoverItem(item: GalleryItem) {
    this.hoverItem = item;
    this.check.detectChanges();
  }

  setHoverContextOpenItem(item: GalleryItem, contextItem: GalleryItem) {
    const currentIsContextItem = this.hoverContextOpenItem?.asset.id === contextItem.asset.id;
    if (!item && !currentIsContextItem) return;

    this.hoverContextOpenItem = item;
    this.check.detectChanges();
  }

  notAllItemsHaveCaptureTime() {
    if (!this.allItems()) return;
    return this.allItems().some(item => !getCaptureTime((item.asset.version_data.data as AssetImageTypeDataDto)?.exif));
  }

  someItemsHaveCaptureTime() {
    if (!this.allItems()) return;
    return this.allItems().some(item => getCaptureTime((item.asset.version_data.data as AssetImageTypeDataDto)?.exif));
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
